import * as CookieManager from "@/lib/managers/CookieManager";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';

export interface StreakStorage {
  getCurrentStreak(): number;
  getMaxStreak(): number;
  getLastPlayed(): string | null;
  getLastWinDate(): string | null;
  getPreviousStreak(): number | null;
  setStreak(value: number): void;
  setMaxStreak(value: number): void;
  setLastPlayed(date: string): void;
  setLastWinDate(date: string | null): void;
  setPreviousStreak(value: number): void;
}

export class CookieStreakStorage implements StreakStorage {
  // Cache last win date when getting it to ensure consistency
  private cachedLastWinDate: string | null = null;
  private migrationCompleted: boolean = false;

  getCurrentStreak(): number {
    this.ensureMigration(); // Trigger migration on first access
    const streak = modeAwareStorage.getStreak();
    console.log('Getting streak via mode-aware storage:', streak);
    return streak ?? 0;
  }

  getMaxStreak(): number {
    console.log('Getting max streak from storage');
    const maxStreak = modeAwareStorage.getMaxStreak();
    console.log('Retrieved max streak via mode-aware storage:', maxStreak);
    return maxStreak ?? 0;
  }

  getLastPlayed(): string | null {
    console.log('Getting last played date');
    const lastPlayed = modeAwareStorage.getLastPlayed();
    console.log('Retrieved last played via mode-aware storage:', lastPlayed);
    return lastPlayed;
  }

  getLastWinDate(): string | null {
    console.log('Getting last win date');
    
    // Use cached value if available
    if (this.cachedLastWinDate !== null) {
      console.log('Retrieved cached last win date:', this.cachedLastWinDate);
      return this.cachedLastWinDate;
    }
    
    const lastWinDate = modeAwareStorage.getLastWinDate();
    console.log('Retrieved last win date via mode-aware storage:', lastWinDate);
    
    // Cache the value
    this.cachedLastWinDate = lastWinDate;
    console.log('Retrieved and cached last win date:', lastWinDate);
    return lastWinDate;
  }

  setStreak(value: number): void {
    console.log('Setting streak via mode-aware storage:', value);
    modeAwareStorage.setStreak(value);
  }

  setMaxStreak(value: number): void {
    console.log('Setting max streak to:', value);
    modeAwareStorage.setMaxStreak(value);
  }

  setLastPlayed(date: string): void {
    console.log('Setting last played to:', date);
    modeAwareStorage.setLastPlayed(date);
  }

  setLastWinDate(date: string | null): void {
    console.log('Setting last win date to:', date);
    // Update the cache
    this.cachedLastWinDate = date;
    
    if (!date) {
      // Clear the cookie when null to avoid storing "null" string
      modeAwareStorage.removeCookie('last_win_date');
      return;
    }
    
    console.log('Setting last win date via mode-aware storage:', date);
    modeAwareStorage.setLastWinDate(date);
  }

  getPreviousStreak(): number | null {
    console.log('Getting previous streak');
    const previousStreakValue = modeAwareStorage.getCookie('previous_streak');
    const previousStreak = previousStreakValue ? parseInt(previousStreakValue) : null;
    console.log('Retrieved previous streak via mode-aware storage:', previousStreak);
    return previousStreak;
  }

  setPreviousStreak(value: number): void {
    console.log('Setting previous streak to:', value);
    modeAwareStorage.setCookie('previous_streak', value.toString());
  }

  /**
   * Migrates shared streak data to daily mode for backward compatibility
   * This ensures existing users don't lose their streak progress
   */
  public ensureMigration(): void {
    if (this.migrationCompleted) return;

    try {
      // IMPORTANT: Daily/custom modes already use the base 'babblegram_' prefix for streaks.
      // Treating those as "legacy" and deleting them wipes real data. If the current
      // streak prefix is the base prefix, skip migration entirely to preserve values.
      const currentPrefix = CookieManager.getStreakSpecificPrefix?.() || 'babblegram_';
      if (currentPrefix === 'babblegram_') {
        console.log('🔄 Streak uses base prefix (daily/custom) — skipping migration to avoid data loss');
        this.migrationCompleted = true;
        return;
      }

      console.log('🔄 Checking for streak data migration needs');
      
      // Check if there's legacy shared streak data that needs migration
      const legacyStreak = this.getLegacySharedValue('streak');
      const legacyMaxStreak = this.getLegacySharedValue('max_streak');
      const legacyLastWinDate = this.getLegacySharedValue('last_win_date');
      const legacyPreviousStreak = this.getLegacySharedValue('previous_streak');

      const hasLegacyData = legacyStreak !== null || legacyMaxStreak !== null || 
                           legacyLastWinDate !== null || legacyPreviousStreak !== null;

      if (hasLegacyData) {
        console.log('🔄 Found legacy streak data, migrating to mode-specific prefix:', {
          legacyStreak,
          legacyMaxStreak,
          legacyLastWinDate,
          legacyPreviousStreak
        });

        // Migrate only if target keys are truly empty (0 means empty for numbers)
        if (legacyStreak !== null && CookieManager.getStreak() === 0) {
          CookieManager.setStreak(parseInt(legacyStreak));
        }
        if (legacyMaxStreak !== null && CookieManager.getMaxStreak() === 0) {
          CookieManager.setMaxStreak(parseInt(legacyMaxStreak));
        }
        if (legacyLastWinDate !== null && CookieManager.getLastWinDate() === null) {
          CookieManager.setLastWinDate(legacyLastWinDate);
        }
        if (legacyPreviousStreak !== null && CookieManager.getPreviousStreak() === null) {
          CookieManager.setPreviousStreak(parseInt(legacyPreviousStreak));
        }

        // Clean up legacy cookies after successful migration
        console.log('🧹 Cleaning up legacy cookies after migration');
        this.deleteLegacySharedValue('streak');
        this.deleteLegacySharedValue('max_streak');
        this.deleteLegacySharedValue('last_win_date');
        this.deleteLegacySharedValue('previous_streak');
        
        console.log('✅ Migration completed and legacy cookies cleaned up');
      } else {
        console.log('🔄 No legacy streak data found, skipping migration');
      }

      this.migrationCompleted = true;
    } catch (error) {
      console.error('❌ Error during streak data migration:', error);
      this.migrationCompleted = true; // Don't retry on error
    }
  }

  /**
   * Gets a value from the legacy shared cookie format
   */
  private getLegacySharedValue(key: string): string | null {
    const legacyKey = `babblegram_${key}`;
    const value = document.cookie
      .split('; ')
      .find(row => row.startsWith(legacyKey + '='))
      ?.split('=')[1];
    return value || null;
  }

  /**
   * Deletes a legacy shared cookie
   */
  private deleteLegacySharedValue(key: string): void {
    const legacyKey = `babblegram_${key}`;
    document.cookie = `${legacyKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}
